import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import PtfButton from "@/components/PtfButton";
import { Reveal } from "@/components/motion/Reveal";
import { EvidenceChip } from "@/components/Evidence";

const Eyebrow = ({ children }) => (
  <p className="text-[0.7rem] uppercase tracking-[0.28em] mb-5 flex items-center gap-3 text-ptf-primary/60">
    <span className="h-px w-8 inline-block bg-ptf-primary/40" />
    {children}
  </p>
);

function Quote({ children }) {
  return (
    <blockquote className="my-8 border-l-2 border-ptf-accent pl-6 font-serif-ed italic text-2xl text-ptf-secondary leading-snug">
      "{children}"
    </blockquote>
  );
}

const SECTIONS = [
  {
    title: "The person",
    body: (
      <>
        <p className="text-lg text-ptf-text/75 leading-relaxed">
          "Hi, my name is Laxmi Negi." That is how she introduces herself in the Project
          Dawn field documentation. She lives in Khirdwari, a small village in the
          Champawat district of Uttarakhand, one of the three villages the project
          adopted in the Ladhiya Valley.
        </p>
        <Quote>My village lacks basic amenities that include clean drinking water, hospital facility, electricity or proper education facilities. Due to lack of economic stability it is also hard for many of us to migrate and find proper jobs.</Quote>
      </>
    ),
  },
  {
    title: "The place",
    body: (
      <>
        <p className="text-lg text-ptf-text/75 leading-relaxed">
          To reach Khirdwari, one crosses the Ladhiya river thirteen times. Rain can
          raise the water without warning into a flood-like situation. There is a
          rope-way across the valley. The last time it worked was in 2017.
        </p>
        <Quote>This is the only source of water we have. It tends to get destroyed during landslides, after which the river remains the only option.</Quote>
        <p className="text-ptf-text/70 leading-relaxed">
          A single water tank fills in the rainy season; the village uses that water
          for the whole year.
        </p>
      </>
    ),
  },
  {
    title: "The challenge",
    body: (
      <>
        <p className="text-lg text-ptf-text/75 leading-relaxed">
          The nearest hospital is 70 kilometres away. Expectant mothers depend on
          their elders. There is no proper hospital facility. The local school was
          given two computers, but they were kept for display: there was no
          electricity to run them.
        </p>
        <p className="mt-5 text-ptf-text/70 leading-relaxed">
          Menstruation carries a taboo the village calls <em>Mahawari</em>: for five
          days a woman is not allowed to enter her own house, even if it rains, and
          purification rituals follow before she may return.
        </p>
      </>
    ),
  },
  {
    title: "The field visit",
    body: (
      <>
        <p className="text-lg text-ptf-text/75 leading-relaxed">
          The Project Dawn team trekked 70 kilometres to reach the villages and lived
          there, recording what residents said they needed before anything was
          promised. When the women were kept outside during Mahawari, Nikhil Pratap
          Singh stood beside them, and accepted the same purification ritual they
          did. Respect first; change after.
        </p>
      </>
    ),
  },
  {
    title: "What changed",
    body: (
      <>
        <p className="text-lg text-ptf-text/75 leading-relaxed">
          The valley's documented interventions reached Khirdwari's families: a
          1.5 kW solar panel, a hand pump, the river trolley repaired, medicines and
          sanitary pads distributed, stationery for the school children, and the
          school's computers switched on for the first time in nine years. Through
          the pandemic, ration kits, pads and clothes followed.
        </p>
      </>
    ),
  },
  {
    title: "What remains",
    body: (
      <>
        <p className="text-lg text-ptf-text/75 leading-relaxed">
          Most of it. The hospital is still 70 kilometres away, the rope-way still
          does not run, and the river still floods. One pilot does not undo
          isolation, which is exactly why the work, and this story, continue.
        </p>
      </>
    ),
  },
];

export default function LaxmiStory() {
  return (
    <>
      <Seo title="Laxmi's Story: Khirdwari, Uttarakhand" path="/dawn/laxmi" description="Laxmi Negi lives in Khirdwari, Champawat, Uttarakhand: 13 river crossings from the road and 70 km from a hospital. Her documented story from the Project Dawn field records." />
      <PageHero
        eyebrow="Field story · Khirdwari, Uttarakhand"
        titleLines={["Laxmi's", "story."]}
        intro="Thirteen river crossings from the road, seventy kilometres from a hospital. Laxmi Negi's account of Khirdwari, told in her own documented words."
        image="/dawn/laxmi-family.jpg"
      />

      <section className="bg-ptf-bg py-14 md:py-20">
        <div className="mx-auto max-w-[900px] px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-12 gap-4 items-start">
            <figure className="col-span-5 sm:col-span-4">
              <img src="/dawn/laxmi-portrait.jpg" alt="Laxmi Negi, Khirdwari, from the Project Dawn field documentation" className="w-full aspect-square object-cover" loading="lazy" />
              <figcaption className="mt-2 text-[0.62rem] uppercase tracking-[0.12em] text-ptf-text/50">Laxmi Negi · Khirdwari</figcaption>
            </figure>
            <figure className="col-span-7 sm:col-span-8">
              <img src="/dawn/laxmi-family.jpg" alt="A family of Khirdwari by the river, a field photograph" className="w-full aspect-[16/10] object-cover" loading="lazy" />
              <figcaption className="mt-2 flex items-center justify-between gap-3 text-[0.62rem] uppercase tracking-[0.12em] text-ptf-text/50">
                Field photographs · Project Dawn documentation
                <EvidenceChip status="DOCUMENTED" />
              </figcaption>
            </figure>
          </div>
          <p className="mt-6 text-[0.68rem] text-ptf-text/50 leading-relaxed">
            All quotes and details on this page come from the Project Dawn field
            documentation. Nothing is dramatised, and nothing is invented.
          </p>
        </div>
      </section>

      <section className="bg-ptf-bg pb-16 md:pb-24">
        <div className="mx-auto max-w-[900px] px-5 sm:px-8 lg:px-12">
          <div className="space-y-0">
            {SECTIONS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.04}>
                <div className="py-8 border-t border-ptf-primary/12">
                  <Eyebrow>{String(i + 1).padStart(2, "0")}</Eyebrow>
                  <h2 className="font-display uppercase text-3xl tracking-tight text-ptf-primary leading-none">{s.title}</h2>
                  <div className="mt-4">{s.body}</div>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-6 grid grid-cols-2 gap-4">
            <figure>
              <img src="/dawn/school-computers.jpg" alt="Khirdwari's school children at the computers, switched on at last" className="w-full aspect-[4/3] object-cover" loading="lazy" />
              <figcaption className="mt-2 text-[0.62rem] uppercase tracking-[0.12em] text-ptf-text/50">The school's computers, switched on at last</figcaption>
            </figure>
            <figure>
              <img src="/dawn/trolley-repair.jpg" alt="The repaired river trolley crossing the Ladhiya" className="w-full aspect-[4/3] object-cover" loading="lazy" />
              <figcaption className="mt-2 text-[0.62rem] uppercase tracking-[0.12em] text-ptf-text/50">The river trolley, repaired</figcaption>
            </figure>
          </div>
        </div>
      </section>

      <section className="bg-ptf-primary text-ptf-bg py-20 md:py-28 grain">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
          <h2 className="font-display font-extrabold uppercase text-4xl md:text-5xl tracking-tight leading-[0.95]">Her story is also<br />the film's story.</h2>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <PtfButton to="/dawn/film" variant="accent">Dawn: A Project of Belief</PtfButton>
            <PtfButton to="/dawn/valley-of-river" variant="outlineLight">The Valley of River</PtfButton>
          </div>
        </div>
      </section>
    </>
  );
}
